import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { actions } from '../../store/attachmentTemplate';
import { getSurveys } from 'store/surveys/actions';
import { produce } from 'immer';
import FormController from '../../common/formController';
import Joi from 'joi';
import validators from './validators';
import { constants } from 'views/Campaign';
import LicenseCheck from 'LicenseCheck';
import PermissionCheck from 'PermissionsCheck.js';
import { ProductType } from 'constants.js';
import { getSchemas } from 'store/schema/actions';
import { isReadOnlyMode, encodeTemplateContent } from 'store/helpers/helperFunctions';
import { useLanguageSettings } from '../Settings/useLanguageSettings'
import { getEnvVar } from 'helpers';
import { shouldPreserveCopyTemplateState } from 'helpers/templateCopyGuard';

const {
  getAttachmentTemplate,
  setCurrentAttachmentTemplate,
  updateAttachmentTemplate,
  createAttachmentTemplate,
  attachmentAutoSaveTimerStart,
  setNextStep,
  unsetNextStep,
  setCopyCurrentAttachmentTemplate,
  getAttachmentTemplateFile,
  downloadedAttachmentTemplateFile,
  setAttachmentTestDownload
} = actions;

const {
  SCHEDULED,
  ON_DEMAND,
  ON_EVENT,
  INTERNAL_SURVEY_RESPONSE,
  EXTERNAL_SURVEY_RESPONSE
} = constants.campaignOccurrenceTypes;

let formController = new FormController();

// Change implemented as part of Bug 1078741:
// In ADM UI, SRT-enabled templates are restricted for Guest Insights templates (Communication & Attachment).
// This logic can be reverted once Guest Insights supports Survey Response campaigns.
const isGuestInsightTemplate = template =>
  template?.productType === ProductType.GUEST_INSIGHTS ||
  template?.productName === 'GuestInsight';

export default function useTemplate(nextPath, backPath, shouldLoadSurveys = true) {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { templateUuid: originalTemplateUuid } = useParams();
  const isAutoSaveDisabled = getEnvVar('ADM_DISABLE_AUTO_SAVE') === 'true';
  const isAutoSaveTimerRunning = useSelector(store => store.attachmentTemplate.isAutoSaveTimerRunning);
  const template = useSelector(store => store.attachmentTemplate.current);
  const templateStarters = useSelector(
    store => store.attachmentTemplate.starters
  );

  const isTemplateSaving = useSelector(
    store => store.attachmentTemplate.isTemplateSaving
  );
  const isTemplateSaved = useSelector(
    store => store.attachmentTemplate.isTemplateSaved
  );
  const shouldAutoSave = useSelector(
    store => store.attachmentTemplate.shouldAutoSave
  );
  const isAutoSaveSuccess = useSelector(
    store => store.attachmentTemplate.isAutoSaveSuccess
  );

  const isLoading = useSelector(store => store.attachmentTemplate.getting);

  const isInTemplateDesignDialog = useSelector(
    store => store.attachmentTemplate.isInTemplateDesignDialog
  );
  const supportedEventTypeOptions = useSelector(
    store => store.events.supportedEventTypes.options
  );
  const productIntegrationOptions = useSelector(
    store => store.events.supportedEventTypes.productIntegrationOptions
  );
  const allEventTypeCategoryOptions = useSelector(
    store => store.events.supportedEventTypes.allEventTypeCategoryOptions
  );
  const supportedScheduleTypeOptions = useSelector(
    store => store.events.supportedScheduledTypes
  );
  const supportedOnDemandTypeOptions = useSelector(
    store => store.events.supportedOnDemandTypes
  );
  const supportedInternalSurveyResponseTypeOptions = useSelector(
    store => store.events.supportedInternalSurveyResponseTypes
  );
  const supportedExternalSurveyResponseTypeOptions = useSelector(
    store => store.events.supportedExternalSurveyResponseTypes
  );
  const allSurveys = useSelector(store => store.survey.list);

  const currentTenantId = useSelector(
    store => store.user.selectedTenant.tenantId
  );
  const currentBusinessContextId = useSelector(
    store => store.user.selectedBusinessContext.businessContextId
  );
  const listManagementSchema = useSelector(
    store => store.schema.listManagementSchema
  );
  const guestInsightSchema = useSelector(
    store => store.schema.guestInsightSchema
  );

  const settingsMetaDataStore = useSelector(
    store => store.settings.settingsMetaData.data
  );

  // Test attachment template state (matches print template pattern)
  const isTestDownloadError = useSelector(
    store => store.attachmentTemplate.isTestDownloadError
  );
  const isTestTemplateLoading = useSelector(
    store => store.attachmentTemplate.attachmentFile.getting
  );
  const gettingFileSucceeded = useSelector(
    store => store.attachmentTemplate.attachmentFile.gettingFileSucceeded
  );
  const attachmentFileBlob = useSelector(
    store => store.attachmentTemplate.attachmentFile.attachmentFileBlob
  );

  const licenses = LicenseCheck();
  const { isLicenseLoading, canAccessSurvey } = licenses;

  const permissions = PermissionCheck();
  const { hasPermission } = permissions;

  const [validationErrors, setValidationErrors] = useState({});
  const [errorValidationSegment, setErrorValidationSegment] = useState(null);
  const [templateUuid, setTemplateUuid] = useState(originalTemplateUuid);
  const { templateLanguageContent } = useLanguageSettings();
  const [thisTemplate, setThisTemplate] = useState({
    type: '',
    name: '',
    defaultTemplate: false,
    promotional: false,
    surveyResponseTemplate: false,
    languageCode: settingsMetaDataStore?.languageCode
  });

  const validate = (saveAttempted = false) => {
    let segment = errorValidationSegment;
    const joiValidators = Joi.object(validators[segment]);
    const validation = joiValidators.validate(thisTemplate, {
      abortEarly: false,
      allowUnknown: true // allow unknown keys that will be ignored
    });
    let errors = formController.getFormErrors(validation, { saveAttempted });
    setValidationErrors(errors);
    return errors;
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (!isLoading) {
      const queryParams = new URLSearchParams(location.search);
      const businessContextId = queryParams.get('businessContextId');
      
      dispatch(getAttachmentTemplate({ 
        templateUuid,
        ...(businessContextId && { businessContextId })
      }));
    }
    if (Object.keys(template).length > 0) {
      formController = new FormController();
      setThisTemplate(prevTemplate => {
        const queryParams = new URLSearchParams(location.search);
        const isCopyMode = queryParams.get('copy') === 'true';
        const isUnsavedCopyFlow = shouldPreserveCopyTemplateState({
          isCopyMode,
          previousTemplate: prevTemplate,
          incomingTemplate: template
        });

        if (isUnsavedCopyFlow) {
          return prevTemplate;
        }

        return { ...prevTemplate, ...template };
      });
    }
  }, [templateLanguageContent, location.search]);

  useEffect(() => {
    if (Object.keys(template).length > 0) {
      formController = new FormController();
      setThisTemplate(prevTemplate => {
        const queryParams = new URLSearchParams(location.search);
        const isCopyMode = queryParams.get('copy') === 'true';
        const isUnsavedCopyFlow = shouldPreserveCopyTemplateState({
          isCopyMode,
          previousTemplate: prevTemplate,
          incomingTemplate: template
        });

        if (isUnsavedCopyFlow) {
          return prevTemplate;
        }

        return { ...prevTemplate, ...template };
      });
    }

    if (!templateUuid) {
      setThisTemplate(prevTemplate => {
        const queryParams = new URLSearchParams(location.search);
        const isCopyMode = queryParams.get('copy') === 'true';
        const isUnsavedCopyFlow = shouldPreserveCopyTemplateState({
          isCopyMode,
          previousTemplate: prevTemplate,
          incomingTemplate: template
        });

        if (isUnsavedCopyFlow) {
          return prevTemplate;
        }

        return {
          ...prevTemplate,
          ...template,
          hasMovedToNextStep: template.hasMovedToNextStep || false
        };
      });
    }
  }, [template, templateUuid, location.search]);

  useEffect(() => {
    if (
      thisTemplate.productType &&
      thisTemplate.productType === ProductType.List
    ) {
      setThisTemplate({
        ...thisTemplate,
        productCategory: listManagementSchema?.eventProductCategory || '',
        productName: listManagementSchema?.eventProductName || '',
        eventTypeCategory: listManagementSchema?.eventTypeCategory || '',
        schemaIdentifier:
          listManagementSchema?.eventProductCategory +
            '.' +
            listManagementSchema?.eventProductName +
            '.' +
            listManagementSchema?.eventTypeCategory || ''
      });
    }
  }, [listManagementSchema]);

  useEffect(() => {
    if (
      thisTemplate.productType &&
      thisTemplate.productType === ProductType.GUEST_INSIGHTS
    ) {
      setThisTemplate({
        ...thisTemplate,
        productCategory: guestInsightSchema?.eventProductCategory || '',
        productName: guestInsightSchema?.eventProductName || '',
        eventTypeCategory: guestInsightSchema?.eventTypeCategory || '',
        schemaIdentifier:
          guestInsightSchema?.eventProductCategory +
            '.' +
            guestInsightSchema?.eventProductName +
            '.' +
            guestInsightSchema?.eventTypeCategory || ''
      });
    }
  }, [guestInsightSchema]);

  // Handle PDF file opening (matches print template pattern)
  useEffect(() => {
    if (!isTestTemplateLoading) {
      if (gettingFileSucceeded && attachmentFileBlob.type === 'application/pdf') {
        const file = new Blob([attachmentFileBlob], { type: 'application/pdf' });  
        const fileURL = URL.createObjectURL(file);  
        window.open(fileURL);  
      }
      dispatch(downloadedAttachmentTemplateFile());
    }
  }, [gettingFileSucceeded]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const copy = queryParams.get('copy');
    
    if (copy === 'true') {
      setTemplateUuid(undefined);
      
      let templateToCopy = Object.assign(
        {},
        template
      );
      if (templateToCopy && (templateToCopy.uuid || templateToCopy.id || templateToCopy.name)) {
        
        if (templateToCopy.campaignOccurrenceType === "ON_EVENT" || templateToCopy.campaignOccurrenceType === "SCHEDULED") {
          templateToCopy.isTypeEditable = true;
          let currentEventType = supportedEventTypeOptions.filter(option => option.id === templateToCopy.schemaIdentifier)[0];
          templateToCopy.isLongTermStorage = currentEventType?.longTermStorage;
        }
        
        delete templateToCopy.id;
        delete templateToCopy.uuid;
        delete templateToCopy.defaultTemplate;
        templateToCopy._isCopy = true;
        templateToCopy._isCopyTemplate = true;
        templateToCopy.name = `${templateToCopy.name || ''}`.endsWith('-copy')
          ? `${templateToCopy.name || ''}`
          : `${templateToCopy.name || ''}-copy`;
        templateToCopy.active = true;
        templateToCopy.schemaIdentifier = templateToCopy.schemaIdentifier
          ? templateToCopy.schemaIdentifier
          : templateToCopy.productCategory
          ? `${templateToCopy.productCategory}.${templateToCopy.productName}.${templateToCopy.eventTypeCategory}`
          : '';
        // Update businessContextId for copied templates
        templateToCopy.businessContextId = currentBusinessContextId;
        
        setThisTemplate(prevTemplate => ({
          ...templateToCopy,
          hasMovedToNextStep: prevTemplate.hasMovedToNextStep || false
        }));
        
        const newUrl = '/attachment-templates/details';
        window.history.replaceState({}, '', newUrl);
      }
    }
  }, [location.search, template.id, template.uuid, setCopyCurrentAttachmentTemplate, supportedEventTypeOptions]);

  const goNext = event => {
    const errors = validate(true);
    if (errors.meta.clean) {
      dispatch(
        setCurrentAttachmentTemplate({
          ...thisTemplate,
          hasMovedToNextStep: true
        })
      );
      formController = new FormController();
      if (
        nextPath ||
        (event.currentTarget.attributes.link &&
          event.currentTarget.attributes.link.value)
      ) {
        history.push(
          `${nextPath || event.currentTarget.attributes.link.value}${
            (templateUuid && !(thisTemplate._isCopy || thisTemplate._isCopyTemplate)) ? `/${templateUuid}` : ''
          }`
        );
      } else {
        setThisTemplate({
          ...thisTemplate,
          hasMovedToNextStep: true
        });
        dispatch(setNextStep());
      }
    }
    startAutoSaveTimer();
  };

  const resetValidationErrors = () => {
    setValidationErrors({});
    setThisTemplate({
      type: '',
      name: '',
      promotional: false,
      surveyResponseTemplate: false
    });
  };

  useEffect(() => {
    !isLicenseLoading &&
      shouldLoadSurveys &&
      canAccessSurvey &&
      hasPermission('Rguest_readSurvey') &&
      dispatch(getSurveys());
  }, [isLicenseLoading, shouldLoadSurveys]);

  const goBack = event => {
    dispatch(setCurrentAttachmentTemplate({ ...thisTemplate }));
    if (
      backPath ||
      (event.currentTarget.attributes.link &&
        event.currentTarget.attributes.link.value)
    ) {
      history.push(
        `${backPath || event.currentTarget.attributes.link.value}${
          templateUuid ? `/${templateUuid}` : ''
        }`
      );
    } else {
      dispatch(unsetNextStep());
    }
  };

  const navBackOnError = path => {
    if (
      thisTemplate.hasOwnProperty('hasMovedToNextStep') &&
      !thisTemplate.hasMovedToNextStep &&
      !thisTemplate.name
    ) {
      dispatch(
        setCurrentAttachmentTemplate({
          ...thisTemplate,
          hasMovedToNextStep: false
        })
      );
      history.push(path);
    }
  };

  const onTabChange = () => {
    dispatch(setCurrentAttachmentTemplate(thisTemplate));
  };

  // setByPath({}, 'key.key', 123);
  const setByPath = (obj, path, value) => {
    if (typeof path === 'string') {
      path = path.split('.');
    }
    if (obj && path.length === 1) {
      obj[path] = value;
      return;
    }
    return setByPath(obj[path[0]], path.slice(1), value);
  };

  const handleAutoFillOption = () => {
    setThisTemplate({ ...thisTemplate, campaignOccurrenceType: ON_EVENT });
  };

  const handleChange = events => {
    events = Array.isArray(events) ? events : [events];
    const isCopyFlow = !!(thisTemplate._isCopy || thisTemplate._isCopyTemplate);

    let updatedTemplate = produce(thisTemplate, templateDraft => {
      if (events.length > 0) {
        events.forEach(event => {
          if (isCopyFlow && event.target.name === 'layout') {
            return;
          }

          if (event.target.name === 'campaignOccurrenceType') {
            templateDraft.productName = '';
            templateDraft.eventType = '';
            templateDraft.schemaIdentifier = '';
            templateDraft.productCategory = '';
            templateDraft.eventTypeCategory = '';
            if (
              event.target.value === SCHEDULED ||
              event.target.value === ON_DEMAND ||
              event.target.value === INTERNAL_SURVEY_RESPONSE ||
              event.target.value === EXTERNAL_SURVEY_RESPONSE
            ) {
              delete templateDraft.eventType;
            }
          }
          setByPath(templateDraft, event.target.name, event.target.value);
          if (isGuestInsightTemplate(templateDraft)) {
            templateDraft.surveyResponseTemplate = false;
          }
        });
      }
    });

    setThisTemplate(updatedTemplate);
    validate();
  };

  const handleAutomationChange = event => {
    const { name, value } = event.target;
    let typeOption = {};
    let templateUpdate = produce(thisTemplate, draft => {
      if (thisTemplate.campaignOccurrenceType === ON_EVENT) {
        typeOption = supportedEventTypeOptions.find(
          option => option.id === value
        );

        draft.eventType = typeOption?.eventType || '';
      } else if (thisTemplate.campaignOccurrenceType === ON_DEMAND) {
        typeOption = supportedOnDemandTypeOptions.find(
          option => option.id === value
        );
      } else if (thisTemplate.campaignOccurrenceType === SCHEDULED) {
        typeOption = supportedScheduleTypeOptions.find(
          option => option.id === value
        );
      } else if (
        thisTemplate.campaignOccurrenceType === INTERNAL_SURVEY_RESPONSE
      ) {
        typeOption = supportedInternalSurveyResponseTypeOptions.find(
          option => option.id === value
        );
      } else if (
        thisTemplate.campaignOccurrenceType === EXTERNAL_SURVEY_RESPONSE
      ) {
        typeOption = supportedExternalSurveyResponseTypeOptions.find(
          option => option.id === value
        );
      } else {
      // No campaignOccurrenceType - check productIntegrationOptions and supportedOnDemandTypeOptions
      typeOption = productIntegrationOptions?.find(
        option => option.id === value
      );
      if (!typeOption) {
        typeOption = supportedOnDemandTypeOptions?.find(
          option => option.id === value
        );
      }
      if (!typeOption) {
        typeOption = allEventTypeCategoryOptions?.find(
          option => option.id === value || option.schemaIdentifier === value
        );
      }
    }

      const schemaIdentifier = typeOption?.schemaIdentifier || value || '';
      const [schemaProductCategory, schemaProductName, schemaEventTypeCategory] =
        `${schemaIdentifier}`.split('.');

      draft.productCategory =
        typeOption?.productCategory || schemaProductCategory || '';
      draft.productName = typeOption?.productName || schemaProductName || '';
      draft.eventTypeCategory =
        typeOption?.eventTypeCategory || schemaEventTypeCategory || '';
      draft.schemaIdentifier = schemaIdentifier;
      draft.schemaId = typeOption?.schemaId || '';
      setByPath(draft, name, value);
      if (isGuestInsightTemplate(draft)) {
        draft.surveyResponseTemplate = false;
      }
    });
    setThisTemplate(templateUpdate);
    validate(false);
  };

  const handlePermissions = checkPermission => {
    return hasPermission(checkPermission);
  };

  const modifyPageLayout = (thisTemplate) => {
    let rawHtml = thisTemplate.jsonContent.html;
    let bodyRegex = /(<body.*?>)([\s\S]*?)(<\/body>)/i;  
    let modifiedHtml = rawHtml.replace(bodyRegex, function(match, openTag, bodyContent, closeTag) {  
      const newBodyContent = `{{#each ${thisTemplate.pageIterator}}}<div style="page-break-after: always;">` + bodyContent + `</div>{{/each}}`;  
      return openTag + newBodyContent + closeTag;  
    });
    return modifiedHtml;
  }

  const saveTemplate = () => {
    const errors = validate(true);
    if (errors.meta.clean) {
      const isCopyFlow = !!(thisTemplate._isCopy || thisTemplate._isCopyTemplate);
      let modifiedHtml = modifyPageLayout(thisTemplate);
      dispatch(setCurrentAttachmentTemplate({
        ...thisTemplate,
        promotional: thisTemplate.promotional ?? false,
        surveyResponseTemplate: isGuestInsightTemplate(thisTemplate)
          ? false
          : thisTemplate.surveyResponseTemplate ?? false,
        ...((thisTemplate.productType === ProductType.List || thisTemplate.productType === ProductType.GUEST_INSIGHTS) && {
          defaultTemplate: false
        }),
        ...(thisTemplate.pageIterator && thisTemplate.pageIterator != '' && {
          jsonContent: { ...thisTemplate.jsonContent, html: modifiedHtml },
          content: modifiedHtml
        })
      }));
      if (thisTemplate.uuid && !isCopyFlow) {
        dispatch(updateAttachmentTemplate());
      } else {
        dispatch(createAttachmentTemplate());
      }
      formController = new FormController();
    }
  };

  const closeTemplate = () => {
    formController = new FormController();
    history.push('/attachment-templates');
  };

  const onBlur = e => {
    formController.onBlur(e);
    validate();
  };

  const onFocus = e => {
    formController.onFocus(e);
    validate();
  };

  const shouldShowError = name => {
    return formController.shouldShowError(validationErrors, name);
  };

  const getErrorMessage = name => {
    return formController.getErrorMessage(validationErrors, name);
  };

  const showFormSaveError = () => {
    return formController.showFormSaveError(validationErrors);
  };

  const updateCurrentErrorValidationSegment = name => {
    setErrorValidationSegment(name);
  };

  const startAutoSaveTimer = () => {
    if (template.tenantId === currentTenantId && template.businessContextId === currentBusinessContextId
      && !isLoading && !isAutoSaveTimerRunning && !isAutoSaveDisabled
    ) {
      dispatch(attachmentAutoSaveTimerStart());
    }
  };

  const autoSaveTemplate = () => {
    const errors = validate(true);
    if (errors.meta.clean) {
      const isCopyFlow = !!(thisTemplate._isCopy || thisTemplate._isCopyTemplate);
      let modifiedHtml = modifyPageLayout(thisTemplate);
      dispatch(setCurrentAttachmentTemplate({
        ...thisTemplate,
        promotional: thisTemplate.promotional ?? false,
        surveyResponseTemplate: isGuestInsightTemplate(thisTemplate)
          ? false
          : thisTemplate.surveyResponseTemplate ?? false,
        ...((thisTemplate.productType === ProductType.List || thisTemplate.productType === ProductType.GUEST_INSIGHTS) && {
          defaultTemplate: false
        }),
        ...(thisTemplate.pageIterator && thisTemplate.pageIterator != '' && {
          jsonContent: { ...thisTemplate.jsonContent, html: modifiedHtml },
          content: modifiedHtml
        })
      }));
      if (thisTemplate.uuid && !isCopyFlow) {
        dispatch(updateAttachmentTemplate());
      } else {
        dispatch(createAttachmentTemplate());
      }
      formController = new FormController();
    }
  };

  const toggleDefaultTemplate = () => {
    setThisTemplate({
      ...thisTemplate,
      defaultTemplate: !thisTemplate.defaultTemplate
    });
  };

  const togglePromotional = () => {
    const isCopyFlow = !!(thisTemplate._isCopy || thisTemplate._isCopyTemplate);
    if (isCopyFlow) {
      return;
    }

    setThisTemplate({
      ...thisTemplate,
      promotional: !thisTemplate.promotional
    });
  };

  const toggleSurveyResponseTemplate = () => {
    const isCopyFlow = !!(thisTemplate._isCopy || thisTemplate._isCopyTemplate);
    if (isCopyFlow || isGuestInsightTemplate(thisTemplate)) {
      return;
    }

    setThisTemplate({
      ...thisTemplate,
      surveyResponseTemplate: !thisTemplate.surveyResponseTemplate
    });
  };

  const modifyTags = updatedTags => {
    setThisTemplate({
      ...thisTemplate,
      tags: [...updatedTags]
    });
  };

  const modifyPageIterator = updatedPageIterator => {
    setThisTemplate({
      ...thisTemplate,
      pageIterator: updatedPageIterator
    });
  };

  const isTemplateReadOnly = () => {
    return (
      thisTemplate.uuid &&
      (currentTenantId !== thisTemplate.tenantId ||
        currentBusinessContextId !== thisTemplate.businessContextId)
    ) || isReadOnlyMode();
  };

  const showWarningMessage = () => {
    return (
      isDefaultTemplate() &&
      thisTemplate.tenantId === currentTenantId &&
      thisTemplate.businessContextId === currentBusinessContextId
    );
  };

  const isDefaultTemplate = () => {
    return thisTemplate.uuid && template.defaultTemplate;
  };

  const showDefaultCheckbox = () => {
    return (
      ((currentTenantId === '0' && currentBusinessContextId === 'Default') ||
        thisTemplate.defaultTemplate) && thisTemplate.productType !== ProductType.List && 
        thisTemplate.productType !== ProductType.GUEST_INSIGHTS
    );
  };

  const isTenantDefaultTemplate = () => {
    return (
      (!thisTemplate.defaultTemplate ||
        thisTemplate.tenantId === currentTenantId) &&
      thisTemplate.businessContextId !== currentBusinessContextId
    );
  };

  const handleTypeChange = event => {
    if (event.target.value === ProductType.List || event.target.value === ProductType.GUEST_INSIGHTS) {
      dispatch(getSchemas({ showActive: true }));
    }
    setThisTemplate({
      ...thisTemplate,
      productType: event.target.value,
      surveyResponseTemplate:
        event.target.value === ProductType.GUEST_INSIGHTS
          ? false
          : thisTemplate.surveyResponseTemplate,
      eventType: '',
      productCategory: '',
      productName: '',
      eventTypeCategory: '',
      schemaIdentifier: '',
      subscriberListId: '',
      segmentedListId: ''
    });
  };

  const handleSubscriberListChange = (event, option) => {
    if (!option) {
      setThisTemplate({
        ...thisTemplate,
        subscriberListId: '',
        segmentedListId: ''
      });
      return;
    }
    setThisTemplate({
      ...thisTemplate,
      ...(option.listId
        ? { subscriberListId: option.listId, segmentedListId: option.id }
        : { subscriberListId: option.id, segmentedListId: '' })
    });
  };

  /**
   * Click handler for Test Template button (matches print template pattern)
   * @param {Object} exportedContent - The jsonContent exported from the Unlayer editor
   */
  const onClickTestTemplate = (exportedContent) => {
    let templateToTest = { ...thisTemplate };
    if (exportedContent) {
      templateToTest.jsonContent = exportedContent;
    }
    if (templateToTest.pageIterator && templateToTest.pageIterator != '') {
      let modifiedHtml = modifyPageLayout(templateToTest);
      templateToTest.jsonContent = { ...templateToTest.jsonContent, html: modifiedHtml };
    }
    let encodedTemplate = encodeTemplateContent(templateToTest);
    dispatch(getAttachmentTemplateFile(encodedTemplate));
  };

  /**
   * Click handler to close test download error snackbar (matches print template pattern)
   */
  const onClickTestTemplateClose = () => {
    dispatch(setAttachmentTestDownload(false));
  };

  return {
    thisTemplate,
    templateStarters,
    validationErrors,
    handleChange,
    handleAutomationChange,
    handlePermissions,
    goNext,
    goBack,
    navBackOnError,
    onTabChange,
    saveTemplate,
    closeTemplate,
    onFocus,
    onBlur,
    autoSaveTemplate,
    showFormSaveError,
    getErrorMessage,
    shouldShowError,
    startAutoSaveTimer,
    updateCurrentErrorValidationSegment,
    supportedEventTypeOptions,
    supportedScheduleTypeOptions,
    supportedOnDemandTypeOptions,
    supportedInternalSurveyResponseTypeOptions,
    supportedExternalSurveyResponseTypeOptions,
    productIntegrationOptions,
    allEventTypeCategoryOptions,
    allSurveys,
    isLoading,
    shouldAutoSave,
    isTemplateSaved,
    isAutoSaveSuccess,
    isTemplateSaving,
    isInTemplateDesignDialog,
    resetValidationErrors,
    handleAutoFillOption,
    toggleDefaultTemplate,
    modifyTags,
    modifyPageIterator,
    isTemplateReadOnly,
    showWarningMessage,
    isDefaultTemplate,
    showDefaultCheckbox,
    isTenantDefaultTemplate,
    handleTypeChange,
    handleSubscriberListChange,
    // Test template functions and state (matches print template pattern)
    onClickTestTemplate,
    onClickTestTemplateClose,
    isTestTemplateLoading,
    isTestDownloadError,
    togglePromotional,
    toggleSurveyResponseTemplate
  };
}
